import { DecimalPipe, DOCUMENT, NgClass, NgStyle } from '@angular/common';
import {
    Component,
    Inject,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewEncapsulation,
} from '@angular/core';
import { MatBadge } from '@angular/material/badge';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import {
    MatProgressSpinner,
    ProgressSpinnerMode,
} from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import {
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbTooltip,
} from '@ng-bootstrap/ng-bootstrap';
import { CommentsDialogComponent } from 'app/core/components/popup/comments-dialog/comments-dialog.component';
import { PopupService } from 'app/core/components/popup/popup.service';
import { DashboardService } from 'app/core/services/dashboard.service';
import { ForumService } from 'app/core/services/forum.service';
import { GlobalService } from 'app/core/services/global.service';
import { SecurityService } from 'app/core/services/security.service';
import { ServerService } from 'app/core/services/server.service';
import { ThemeService } from 'app/core/services/theme.service';
import { AlertSettingsService } from 'app/pages/alerts/components/alerts.service';
import { DashboardAccess, User } from 'app/shared/interfaces/UserInterface';
import { EventManagerService } from 'app/shared/utils/event-manager.service';
import { abortUnsubscribe } from 'app/shared/utils/shared-methods.utils';
import { environment } from 'environments/environment';
import { Subject, Subscription } from 'rxjs';
import { AvatarComponent } from '../../core/components/avatar/avatar.component';
import { EntityService } from '../../core/services/entity.service';
import { UserService } from '../../core/services/user.service';
import { UnwrapTagDirective } from '../directives/unwrap-tag.directive';
import { PioneerBadgeComponent } from './star-rating/star-rating.component';

declare let mLayout: any;
@Component({
    selector: 'dashboard-header-nav',
    templateUrl: './dashboard-header-nav.component.html',
    encapsulation: ViewEncapsulation.None,
    styleUrls: ['./dashboard-header-nav.component.scss'],
    imports: [
        UnwrapTagDirective,
        RouterLink,
        MatProgressSpinner,
        MatTooltip,
        NgbDropdown,
        NgbDropdownToggle,
        NgbDropdownMenu,
        NgClass,
        AvatarComponent,
        NgbDropdownItem,
        MatIconButton,
        NgbTooltip,
        NgStyle,
        MatBadge,
        MatIcon,
        PioneerBadgeComponent,
        DecimalPipe,
    ],
})
export class DashboardHeaderNavComponent implements OnInit, OnDestroy {
    public news;
    public formattedToday;
    public numberOfNews;
    public todayNews;
    public menu: any;
    public user: User;
    public info: any;
    public updateUnread = 0;
    public allowedHtlIds: number[] = [];
    protected subscriptions: Subscription[] = [];
    private ngUnsubscribe: Subject<void> = new Subject<void>();
    public selectedAllowedHtl = 0;
    public hideLHGLogo = environment.company === 'lhg' ? false : true;
    public company = environment.company;
    // Progress spinners
    private lastLoadStatusTime: Date | null = null;
    private loadStatusInterval: any;
    private loadStatusDebounceTimer: any;
    protected isForumRoute = false;
    public pinnedDashboards: any[] = [];

    mode: ProgressSpinnerMode = 'determinate';
    value_rooms = 0;
    value_me = 0;
    rooms_tooltip = 'Rooms load status for your current hotel selection: 0%';
    me_tooltip = 'ME load status for your current hotel selection : 0%';
    selectedDashboardId: string | null = null;
    constructor(
        public entityService: EntityService,
        public userService: UserService,
        private serverService: ServerService,
        private popupService: PopupService,
        public router: Router,
        private eventManagerService: EventManagerService,
        @Inject(DOCUMENT) private document: Document,
        private renderer: Renderer2,
        public themeService: ThemeService,
        public globalService: GlobalService,
        private dashboardService: DashboardService,
        public alertsService: AlertSettingsService,
        private securityService: SecurityService,
        public forumService: ForumService
    ) {
        this.ngUnsubscribe = new Subject<void>();
        this.user = this.userService.user;
    }
    public ngOnInit(): void {
        this.router.events.subscribe(() => {
            const bodyNode = this.document.getElementsByTagName('body')[0];
            if (this.themeService.mode === 'dark') {
                this.renderer.removeClass(bodyNode, 'theme--default');
                this.renderer.addClass(bodyNode, 'theme--dark');
            } else {
                this.renderer.removeClass(bodyNode, 'theme--dark');
                this.renderer.addClass(bodyNode, 'theme--default');
            }
        });
        this.getNewDevelopments();
        this.getLoadStatus();
        this._registerChanges();
        this.getPinnedDashboards();
        this.pinnedDashboards = (this.userService.user.pinned || []).map(
            p => p.dashboard_id
        );

        this.eventManagerService.subscribe('dashboardSwitch', response => {
            this.allowedHtlIds = response.data;
            this.selectedAllowedHtl = this.entityService.getSelectedHtlIdsAllowedLength(
                this.allowedHtlIds
            );
        });
        this.eventManagerService.subscribe('selectedEntityUpdate', _response => {
            this.selectedAllowedHtl = this.entityService.getSelectedHtlIdsAllowedLength(
                this.allowedHtlIds
            );
            // Debounce getLoadStatus calls to prevent multiple rapid API calls
            if (this.loadStatusDebounceTimer) {
                clearTimeout(this.loadStatusDebounceTimer);
            }
            this.loadStatusDebounceTimer = setTimeout(() => {
                this.getLoadStatus();
            }, 500); // 500ms debounce
            this.pickRandomPointsMessage();
        });

        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.isForumRoute = event.urlAfterRedirects.includes('/forum');
            }
        });

        // Initialize lastLoadStatusTime
        this.lastLoadStatusTime = new Date();

        // Set up interval to check every minute
        this.loadStatusInterval = setInterval(() => {
            const now = new Date();
            if (this.lastLoadStatusTime) {
                const diff = now.getTime() - this.lastLoadStatusTime.getTime();
                const diffMinutes = diff / (1000 * 60);
                if (diffMinutes >= 5) {
                    this.getLoadStatus();
                }
            } else {
                this.getLoadStatus();
            }
        }, 60000); // 60000 ms = 1 minute
    }

    public checkForUpdates(): void {
        const monthNames = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December',
        ];

        const today = new Date();
        const day = String(today.getDate()).padStart(2, '0');
        const month = monthNames[today.getMonth()];
        const year = today.getFullYear().toString();
        this.formattedToday = day + ' ' + month + ' ' + year;
    }

    public getNumberOfNewAlerts(data): void {
        this.numberOfNews = 0;
        for (const element of data) {
            if (element.news_date === this.formattedToday) {
                this.numberOfNews += 1;
            }
        }
    }

    public getIcon(data): void {
        for (const element of data) {
            element.icon = 'fiber_new';
            element.color = 'rgb(38, 128, 189)';
        }
    }

    private getLoadStatus(): void {
        // 🚪 Block API calls if logout is in progress
        // 🚪 Block API calls if session is invalid or logout is in progress
        if (!this.serverService.shouldMakeApiCalls()) {
            console.log(
                '🚪 [DashboardHeader] Skipping getLoadStatus - session invalid or logout in progress'
            );
            return;
        }

        // Prevent making the same call too frequently (within 2 seconds)
        const now = new Date();
        if (this.lastLoadStatusTime) {
            const timeDiff = now.getTime() - this.lastLoadStatusTime.getTime();
            if (timeDiff < 2000) {
                // Less than 2 seconds
                console.log(
                    '🔄 [DashboardHeader] Skipping getLoadStatus - called too recently'
                );
                return;
            }
        }

        // Validasion so it doesnt call when no hotel selected
        if (
            !this.entityService.selectedEntityIds ||
            this.entityService.selectedEntityIds.length === 0
        ) {
            return;
        }

        console.log('📊 [DashboardHeader] Calling getRoomsMeStatus API');
        this.serverService.post(
            'dashboard/import-control/getRoomsMeStatus',
            { htl_id: this.entityService.selectedHtlId },
            (data: { metric: string; load_percentage: number }[]) => {
                data.forEach(item => {
                    if (item.metric === 'Rooms') {
                        this.value_rooms = item.load_percentage;
                        this.rooms_tooltip =
                            'Rooms load status for your current hotel selection: ' +
                            item.load_percentage +
                            '%';
                    } else if (item.metric === 'M&E') {
                        this.value_me = item.load_percentage;
                        this.me_tooltip =
                            'ME load status for your current hotel selection : ' +
                            item.load_percentage +
                            '%';
                    }
                });
            },
            err => {
                console.error(err);
            },
            this.ngUnsubscribe,
            true
        );
        // Update lastLoadStatusTime
        this.lastLoadStatusTime = new Date();
    }

    public getNewDevelopments(): void {
        // 🚪 Block API calls if session is invalid or logout is in progress
        if (!this.serverService.shouldMakeApiCalls()) {
            console.log(
                '🚪 [DashboardHeader] Skipping getNewDevelopments - session invalid or logout in progress'
            );
            return;
        }

        this.serverService.get(
            'dashboard/summary' + '/getNewDevelopments',
            {},
            (data: any) => {
                const decoded = data.map(el => this.securityService.decodeFormData(el));
                this.checkForUpdates();
                this.getNumberOfNewAlerts(decoded);
                this.getIcon(decoded);
                decoded.map(element =>
                    element.news_date === this.formattedToday
                        ? (element.isNew = true)
                        : (element.isNew = false)
                );
                const transformedData = decoded.reduce((acc, curr) => {
                    const existingDashboard = acc.find(
                        item => item.dashboard === curr.dashboard
                    );
                    if (existingDashboard) {
                        existingDashboard.children.push(curr);
                    } else {
                        acc.push({ dashboard: curr.dashboard, children: [curr] });
                    }

                    return acc;
                }, []);

                this.news = transformedData;
            },
            err => {
                console.error(err);
            },
            this.ngUnsubscribe,
            true
        );
    }

    public init(dashboards: any[]): void {
        if (dashboards == null) return;
        const menus: Array<DashboardCategory> = [];
        let menu_name = '';
        let cpt = -1;
        for (let i = 0; i < dashboards.length; i++) {
            const d = dashboards[i];
            if (menu_name != d['menu_name']) {
                menu_name = d['menu_name'];
                cpt++;
                menus[cpt] = {
                    label: menu_name,
                    name: d['menu_id'],
                    has_presentation: false,
                    has_commercial_plan: false,
                    show_badge: false,
                    children: [],
                };
            }
            if (d.type === 'Custom Presentations') menus[cpt].has_presentation = true;
            if (d.type === 'Settings') menus[cpt].has_presentation = true;
            if (d.type === 'Commercial Plan') menus[cpt].has_commercial_plan = true;
            // For News item (first position of the array), we switch show_badge to true.
            menus[0].show_badge = true;
            menus[cpt].children.push(d);
        }
        this.menu = menus;
        setTimeout(() => {
            mLayout.initHeader();
            $('a[data-bs-toggle=collapse]').on('click', function () {
                // swap chevron
                $(this).children('i').toggleClass('fa-chevron-down fa-chevron-right');
            });
            $('a.list-group-item.m-menu__link').on('click', function () {
                // close menu
                $(this)
                    .closest('li.m-menu__item--open-dropdown.m-menu__item--hover')
                    .removeClass('m-menu__item--open-dropdown m-menu__item--hover');
            });
        }, 500);
        if (this.userService.user && this.serverService.shouldMakeApiCalls()) {
            this.serverService.get(
                'access/getUpdateRead',
                {
                    user: this.userService.user.user_id,
                    update: 'updateRead1',
                },
                (data: { CT: number }[]) => {
                    if (!data[0].CT) this.updateUnread = 1;
                }
            );
        }
    }
    public openQuickSideBar(): void {
        $('#m_quick_sidebar_toggle').trigger('click');
    }
    public openCommentsDialog(): void {
        if (this.info.error) {
            // Do something?
        } else {
            this.popupService.open(
                CommentsDialogComponent as Component,
                undefined,
                node => {
                    // Go to dashboard
                    this.router.navigateByUrl(
                        '/dashboard/' + node.dashboard_id + '#' + node.section_id
                    );
                },
                () => {
                    this._getInfo();
                },
                'xl'
            );
        }
    }
    private _registerChanges(): void {
        this.subscriptions.push(
            this.userService.on_ready.subscribe(ready => {
                if (ready) {
                    this.init(this.userService.user.dashboards);
                }
            })
        );
    }
    private _getInfo(): void {
        // Block API calls if session is invalid or logout is in progress
        if (!this.serverService.shouldMakeApiCalls()) {
            console.log(
                '🚪 [DashboardHeader] Skipping _getInfo - session invalid or logout in progress'
            );
            return;
        }

        abortUnsubscribe(this.ngUnsubscribe);
        this.serverService.post(
            'reader-comments-tool/info',
            {},
            (data: any) => {
                this.info = data;
            },
            error => {
                console.error(error);
                this.info = { badge: 0, error: error };
            },
            this.ngUnsubscribe,
            true
        );
    }
    public ngOnDestroy(): void {
        abortUnsubscribe(this.ngUnsubscribe, true);
        for (const sub of this.subscriptions) {
            sub.unsubscribe();
        }
        if (this.loadStatusInterval) {
            clearInterval(this.loadStatusInterval);
        }
        if (this.loadStatusDebounceTimer) {
            clearTimeout(this.loadStatusDebounceTimer);
        }
    }
    public getNumberOfSelectedHotels(): string {
        const _selectedHotels = this.entityService?.selectedHtlId.filter(htl =>
            this.allowedHtlIds.includes(htl)
        );
        return _selectedHotels.length.toString();
    }

    public switchTheme(theme: string): void {
        this.themeService.themeChanged$.next(theme);
    }
    public openImportControl(): void {
        this.router.navigate(['dashboard/import-control']);
    }
    public getSpinnerColor(value: number): string {
        if (value < 50) {
            return 'warn';
        }
        if (value < 80) {
            return 'accent';
        }
        return 'primary';
    }

    togglePin(dashboardId: number): void {
        const isPinned = this.pinnedDashboards.includes(dashboardId);
        const userId = this.userService.user.user_id;

        const action$ = isPinned
            ? this.dashboardService.unpinDashboard(userId, dashboardId)
            : this.dashboardService.pinDashboard(userId, dashboardId);

        action$.subscribe({
            next: () => {
                if (isPinned) {
                    this.pinnedDashboards = this.pinnedDashboards.filter(
                        id => id !== dashboardId
                    );
                } else {
                    this.pinnedDashboards.push(dashboardId);
                }
            },
            error: err => console.error('Error toggling pin:', err),
        });
    }

    getPinnedDashboards(): any[] {
        const allDashboards = this.menu.flatMap(item => item.children || []);
        return allDashboards.filter(d => this.pinnedDashboards.includes(d.id));
    }

    closeOffCanvas(): void {
        ($('.offcanvas') as any).offcanvas('hide');
    }

    private pointsMessages: string[] = [
        'Nothing stops a real Pulser.',
        'Points tell the story of your commitment.',
        'Building your Pulse journey, one point at a time.',
        'Every action counts.',
        'Consistency beats intensity.',
        'Your dedication powers Pulse forward.',
        'Each point is proof of your impact.',
        'Momentum today, leadership tomorrow.',
        'Pulse legends are built with consistency.',
        'Great insights start with small actions.',
        'You’re shaping the future of Pulse.',
        'The leaderboard is waiting for you.',
        'Push a little further, go a little higher.',
        'Keep the momentum.',
        'Step by step, point by point.',
        'Your journey inspires others.',
        'Small actions, big impact.',
        'Progress looks good on you.',
        'Stay curious, stay consistent.',
        'One point closer to greatness.',
        'Pulse is energy, and you’re charging it',
        'Charles would be proud of those points.',
        'If Charles saw this, he’d say “not bad, eh?”.',
        'Charles-approved progress right here.',
        'Careful… Charles is catching up on the leaderboard.',
    ];

    public pointsMessage: string = '';

    private pickRandomPointsMessage(): void {
        if (!this.pointsMessages?.length) {
            this.pointsMessage = '';
            return;
        }
        const last = this.pointsMessage;
        let idx = Math.floor(Math.random() * this.pointsMessages.length);
        if (this.pointsMessages.length > 1 && this.pointsMessages[idx] === last) {
            idx = (idx + 1) % this.pointsMessages.length;
        }
        this.pointsMessage = this.pointsMessages[idx];
    }

    onUserMenuOpen(isOpen: boolean): void {
        if (isOpen) {
            this.pickRandomPointsMessage();
        }
    }

    openLeaderboard(event: MouseEvent, drop?: any): void {
        event.stopPropagation();
        drop?.close?.();
        this.router.navigate(['/dashboard/leaderboard']);
    }
}
export interface DashboardCategory {
    label: string;
    name: string;
    has_presentation: boolean;
    has_commercial_plan: boolean;
    show_badge: boolean;
    children: Array<DashboardAccess>;
}
